import { withMediaAuth } from "./cloudApi";

export const INSTALLATION_INFO_EVENT = "installationInfoUpdated";
export const SENSOR_TYPES = ["HAECHIE-A", "HAECHIE-AR", "Environment"];
export const MAX_HLINK_ID = 10;
export const MAX_IR_SENSORS = 12;
export const MAX_INSTALL_INFO = 20;
export const MAX_REMARKS = 100;
export const MAX_SENSOR_WRITE_ID = 10;
export const MAX_COORDS = 32;
export const MAX_MAP_MARK = 50;

/** IR / arc sensors shown on installation map (IDs 1–12). */
export const isMapSensorId = (sensorId) => {
  const id = Number(sensorId);
  return id >= 1 && id <= MAX_IR_SENSORS;
};

/** Environment sensors (IDs 21–26) — Environment tab / install info only, never on IR map. */
export const isEnvironmentSensorId = (sensorId) => {
  const id = Number(sensorId);
  return id >= 21 && id <= 26;
};

/** IR sensors shown on Monitoring when COM is connected (IDs 1–12). */
export const isMonitoringSensorId = (sensorId) => isMapSensorId(sensorId);

/** Sensors editable in Settings → Installation Info. */
export const isInstallableSensorId = (sensorId) =>
  isMapSensorId(sensorId) || isEnvironmentSensorId(sensorId);

export const defaultSensorTypeForId = (sensorId, savedType) => {
  if (savedType) return savedType;
  return isEnvironmentSensorId(sensorId) ? "Environment" : "HAECHIE-A";
};

/** Map markers: IR IDs only, never Environment type. */
export const isMapEligibleSensor = (sensorId, sensorType) => {
  if (!isMapSensorId(sensorId)) return false;
  if (sensorType === "Environment") return false;
  return true;
};

const emptyStore = () => ({ version: 1, updated_at: null, hlinks: {} });

export const resolveMediaUrl = (fileUrl) => {
  if (!fileUrl) return null;
  return withMediaAuth(fileUrl);
};

export const parseCoordinates = (value) => {
  if (!value) return null;
  const parts = String(value).replace(";", ",").split(",").map((p) => p.trim());
  if (parts.length < 2) return null;
  const x = Number(parts[0]);
  const y = Number(parts[1]);
  if (Number.isNaN(x) || Number.isNaN(y)) return null;
  return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
};

export const defaultMarkerPosition = (sensorId) => {
  const idx = Math.max(0, (sensorId || 1) - 1);
  const col = idx % 4;
  const row = Math.floor(idx / 4);
  return { x: 12 + col * 22, y: 12 + row * 16 };
};

export const resolveMarkerPosition = (sensorInstall, sensorId) => {
  if (!sensorInstall) return defaultMarkerPosition(sensorId);
  if (sensorInstall.mark_x != null && sensorInstall.mark_y != null) {
    return { x: Number(sensorInstall.mark_x), y: Number(sensorInstall.mark_y) };
  }
  return parseCoordinates(sensorInstall.position_coordinates) || defaultMarkerPosition(sensorId);
};

export const getHlinkInstallation = (store, serial) => {
  if (!store?.hlinks || !serial) return null;
  const rec = store.hlinks[serial];
  if (!rec || rec.serial_number !== serial) return null;
  return rec;
};

export const getSensorInstallation = (store, hlinkSerial, sensorId) => {
  const hlink = getHlinkInstallation(store, hlinkSerial);
  if (!hlink?.sensors) return null;
  return hlink.sensors[String(sensorId)] || null;
};

/**
 * Display payload for Monitoring / Dashboard.
 * Returns null fields when S/N is not stored or does not match (requirement 8–10).
 */
export const getSensorInstallDisplay = (store, serial, sensorId) => {
  const empty = {
    matched: false,
    serial: serial || "",
    sensorId: sensorId ?? "",
    sensorSerial: "",
    location: "",
    coordinates: "",
    mark: "",
    managementNumber: "",
    remarks: "",
    summary: "",
  };
  if (!serial || sensorId == null) return empty;
  const hlink = getHlinkInstallation(store, serial);
  if (!hlink) return empty;
  const install = hlink.sensors?.[String(sensorId)] || {};
  const location = install.installation_location || install.installation_info || "";
  const coordinates = install.position_coordinates || "";
  const mark = install.map_mark_info || "";
  const managementNumber = install.management_number || install.sensor_write_id || "";
  const remarks = install.remarks || "";
  // Sensor unit S/N (future live/store field) — shown on card when present
  const sensorSerial =
    install.sensor_serial ||
    install.sensor_serial_number ||
    install.serial_number ||
    "";
  const parts = [location, coordinates, mark, managementNumber, remarks].filter(Boolean);
  return {
    matched: true,
    serial,
    sensorId,
    sensorSerial: String(sensorSerial).trim(),
    location,
    coordinates,
    mark,
    managementNumber,
    remarks,
    summary: parts.join(" · "),
  };
};

export const getHlinkInstallDisplay = (store, serial) => {
  const hlink = getHlinkInstallation(store, serial);
  if (!hlink) {
    return { matched: false, serial: serial || "", hlinkId: "", location: "", remarks: "" };
  }
  return {
    matched: true,
    serial: hlink.serial_number,
    hlinkId: hlink.hlink_write_id || hlink.management_number || "",
    location: hlink.installation_location || hlink.installation_info || "",
    remarks: hlink.remarks || "",
  };
};

export const fetchInstallationInfo = async (serverIp) => {
  if (!serverIp) return emptyStore();
  try {
    const res = await fetch(`http://${serverIp}:8000/installation-info/`);
    if (!res.ok) return emptyStore();
    const data = await res.json();
    return { ...emptyStore(), ...data, hlinks: data.hlinks || {} };
  } catch {
    return emptyStore();
  }
};

export const saveInstallationInfo = async (serverIp, hlinks, options = {}) => {
  if (!serverIp) throw new Error("Server not available");
  const res = await fetch(`http://${serverIp}:8000/installation-info/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ hlinks }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Save failed");
  }
  const data = await res.json();
  if (!options.silent) {
    window.dispatchEvent(new Event(INSTALLATION_INFO_EVENT));
  }
  return data.data || emptyStore();
};

export const uploadHlinkMapImage = async (serverIp, serial, file) => {
  if (!serverIp || !serial || !file) throw new Error("Missing upload data");
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`http://${serverIp}:8000/installation-map/${encodeURIComponent(serial)}/`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Upload failed");
  }
  const data = await res.json();
  window.dispatchEvent(new Event(INSTALLATION_INFO_EVENT));
  return resolveMediaUrl(data.file_url, serverIp);
};

const mapSensorFromStore = (saved, live) => {
  const sensorId = live.sensor_id;
  const isEnv = isEnvironmentSensorId(sensorId);
  return {
    sensor_id: sensorId,
    com_state: live.com_state,
    sensor_write_id: saved.sensor_write_id || "",
    management_number: saved.management_number || saved.sensor_write_id || "",
    sensor_type: defaultSensorTypeForId(sensorId, saved.sensor_type),
    sensor_serial:
      saved.sensor_serial ||
      saved.sensor_serial_number ||
      live.sensor_serial ||
      live.serial_number ||
      "",
    installation_location: saved.installation_location || saved.installation_info || "",
    installation_info: saved.installation_location || saved.installation_info || "",
    // Env sensors are not placed on the installation map
    position_coordinates: isEnv ? "" : saved.position_coordinates || "",
    map_mark_info: isEnv ? "" : saved.map_mark_info || "",
    mark_x: isEnv ? "" : saved.mark_x ?? "",
    mark_y: isEnv ? "" : saved.mark_y ?? "",
    remarks: saved.remarks || "",
    arc_state: live.arc_state,
    arc_level: live.arc_level,
    ir_temperature: live.ir_temperature,
  };
};

const emptySensorLive = (sensorId) => ({
  sensor_id: sensorId,
  com_state: 0,
  arc_state: 0,
  arc_level: 0,
  ir_temperature: null,
});

export const createEmptySensorRow = (sensorId) => ({
  sensor_id: sensorId,
  com_state: 0,
  sensor_write_id: "",
  management_number: "",
  sensor_type: defaultSensorTypeForId(sensorId),
  sensor_serial: "",
  installation_location: "",
  installation_info: "",
  position_coordinates: "",
  map_mark_info: "",
  mark_x: "",
  mark_y: "",
  remarks: "",
  arc_state: 0,
  arc_level: 0,
  ir_temperature: null,
  manual: true,
});

export const createEmptyHlinkRow = (serial, index, stored = {}) => {
  const location = stored.installation_location || stored.installation_info || "";
  const storedSensors = stored.sensors || {};
  const sensors = Object.keys(storedSensors)
    .map((k) => Number(k))
    .filter((id) => isInstallableSensorId(id))
    .sort((a, b) => a - b)
    .map((id) => mapSensorFromStore(storedSensors[String(id)], emptySensorLive(id)));

  return {
    key: serial,
    index,
    serial_number: serial,
    hlink_ip: stored.hlink_ip || "",
    connected: false,
    hlink_write_id: stored.hlink_write_id || "",
    management_number: stored.management_number || stored.hlink_write_id || "",
    installation_location: location,
    installation_info: location,
    position_coordinates: stored.position_coordinates || "",
    map_mark_info: stored.map_mark_info || "",
    map_image: stored.map_image || "",
    installation_date: stored.installation_date || "",
    remarks: stored.remarks || "",
    sensors,
    manual: true,
  };
};

export const getNextSensorId = (sensors = []) => {
  const used = new Set(sensors.map((s) => Number(s.sensor_id)));
  for (let id = 1; id <= MAX_IR_SENSORS; id += 1) {
    if (!used.has(id)) return id;
  }
  return null;
};

/** Prefer draft/edited form values; only refresh live status from WebSocket rebuild. */
export const mergeFormRowsWithLive = (builtRows, prevRows = []) => {
  const prevBySerial = new Map(prevRows.map((r) => [r.serial_number, r]));
  const builtSerials = new Set(builtRows.map((r) => r.serial_number));

  const merged = builtRows.map((built, idx) => {
    const prev = prevBySerial.get(built.serial_number);
    if (!prev) return { ...built, index: idx + 1 };

    const prevSensorsById = new Map(prev.sensors.map((s) => [Number(s.sensor_id), s]));
    const builtIds = new Set(built.sensors.map((s) => Number(s.sensor_id)));

    const sensors = [
      ...built.sensors.map((liveSensor) => {
        const draft = prevSensorsById.get(Number(liveSensor.sensor_id));
        if (!draft) return liveSensor;
        return {
          ...draft,
          com_state: liveSensor.com_state,
          arc_state: liveSensor.arc_state,
          arc_level: liveSensor.arc_level,
          ir_temperature: liveSensor.ir_temperature,
          manual: draft.manual && liveSensor.com_state !== 1 ? draft.manual : false,
        };
      }),
      ...prev.sensors.filter((s) => !builtIds.has(Number(s.sensor_id))),
    ].sort((a, b) => a.sensor_id - b.sensor_id);

    return {
      ...built,
      index: idx + 1,
      manual: prev.manual && !built.connected,
      hlink_write_id: prev.hlink_write_id,
      management_number: prev.management_number,
      installation_location: prev.installation_location,
      installation_info: prev.installation_info,
      position_coordinates: prev.position_coordinates,
      map_mark_info: prev.map_mark_info,
      map_image: prev.map_image || built.map_image,
      installation_date: prev.installation_date,
      remarks: prev.remarks,
      sensors,
    };
  });

  prevRows.forEach((prev) => {
    if (!builtSerials.has(prev.serial_number)) {
      merged.push({ ...prev, index: merged.length + 1, manual: true });
    }
  });

  return merged.map((row, idx) => ({ ...row, index: idx + 1 }));
};

const buildRowFromDevice = (device, store, index) => {
  const serial = device.device_info.serial_number;
  const stored = getHlinkInstallation(store, serial) || {};
  const storedSensors = stored.sensors || {};
  const liveSensors = (device.sensors || [])
    .filter((s) => isInstallableSensorId(s.sensor_id))
    .sort((a, b) => a.sensor_id - b.sensor_id);

  const liveIds = new Set(liveSensors.map((s) => s.sensor_id));
  const storedOnly = Object.keys(storedSensors)
    .map((k) => Number(k))
    .filter((id) => isInstallableSensorId(id) && !liveIds.has(id))
    .sort((a, b) => a - b);

  const sensors = [
    ...liveSensors.map((s) => mapSensorFromStore(storedSensors[String(s.sensor_id)] || {}, s)),
    ...storedOnly.map((id) => mapSensorFromStore(storedSensors[String(id)], emptySensorLive(id))),
  ].sort((a, b) => a.sensor_id - b.sensor_id);

  const location = stored.installation_location || stored.installation_info || "";
  return {
    key: serial,
    index,
    serial_number: serial,
    hlink_ip: device.device_info.hlink_ip || stored.hlink_ip || "",
    connected: true,
    hlink_write_id: stored.hlink_write_id || "",
    management_number: stored.management_number || stored.hlink_write_id || "",
    installation_location: location,
    installation_info: location,
    position_coordinates: stored.position_coordinates || "",
    map_mark_info: stored.map_mark_info || "",
    map_image: stored.map_image || "",
    installation_date: stored.installation_date || "",
    remarks: stored.remarks || "",
    sensors,
    manual: false,
  };
};

export const buildHlinkFormRows = (livePayload, store) => {
  const devices = livePayload?.devices || livePayload?.data?.devices || [];
  const liveSerials = new Set();
  const rows = [];

  devices
    .filter((d) => d?.device_info?.serial_number)
    .forEach((device, idx) => {
      const serial = device.device_info.serial_number;
      liveSerials.add(serial);
      rows.push(buildRowFromDevice(device, store, idx + 1));
    });

  Object.values(store?.hlinks || {}).forEach((stored) => {
    const serial = stored.serial_number;
    if (!serial || liveSerials.has(serial)) return;
    rows.push(createEmptyHlinkRow(serial, rows.length + 1, stored));
  });

  return rows.map((row, idx) => ({ ...row, index: idx + 1 }));
};

export const rowsToPayload = (rows) =>
  rows.map((row) => ({
    serial_number: row.serial_number,
    hlink_ip: row.hlink_ip,
    hlink_write_id: row.hlink_write_id,
    management_number: row.management_number || row.hlink_write_id,
    installation_location: row.installation_location || row.installation_info,
    installation_info: row.installation_location || row.installation_info,
    position_coordinates: row.position_coordinates,
    map_mark_info: row.map_mark_info,
    map_image: row.map_image,
    installation_date: row.installation_date,
    remarks: row.remarks,
    sensors: row.sensors.map((s) => {
      const isEnv =
        isEnvironmentSensorId(s.sensor_id) || s.sensor_type === "Environment";
      return {
        sensor_id: s.sensor_id,
        sensor_write_id: s.sensor_write_id,
        management_number: s.management_number || s.sensor_write_id,
        sensor_type: isEnvironmentSensorId(s.sensor_id)
          ? "Environment"
          : s.sensor_type,
        sensor_serial: s.sensor_serial || "",
        installation_location: s.installation_location || s.installation_info,
        installation_info: s.installation_location || s.installation_info,
        position_coordinates: isEnv ? "" : s.position_coordinates,
        map_mark_info: isEnv ? "" : s.map_mark_info,
        mark_x: isEnv || s.mark_x === "" ? null : s.mark_x,
        mark_y: isEnv || s.mark_y === "" ? null : s.mark_y,
        remarks: s.remarks,
      };
    }),
  }));

export const buildMapDevices = (livePayload, store) => {
  const devices = livePayload?.devices || livePayload?.data?.devices || [];
  const liveBySerial = new Map();

  devices
    .filter((d) => d?.device_info?.serial_number)
    .forEach((device, index) => {
      const serial = device.device_info.serial_number;
      const hlink = getHlinkInstallation(store, serial) || {};
      const sensors = (device.sensors || [])
        .filter((s) => {
          const install = getSensorInstallation(store, serial, s.sensor_id) || {};
          return isMapEligibleSensor(s.sensor_id, install.sensor_type);
        })
        .map((live) => {
          const install = getSensorInstallation(store, serial, live.sensor_id) || {};
          return {
            ...live,
            install,
            position: resolveMarkerPosition(install, live.sensor_id),
            label: `ID ${live.sensor_id}`,
            locationLabel:
              install.installation_location ||
              install.installation_info ||
              "",
          };
        });
      liveBySerial.set(serial, {
        index: index + 1,
        serial,
        hlink_ip: device.device_info.hlink_ip,
        install: hlink,
        map_image: hlink.map_image || null,
        sensors,
        live: true,
      });
    });

  // Also include H-LINKs saved in Settings that are not currently live
  const storeHlinks = store?.hlinks || {};
  Object.keys(storeHlinks).forEach((serial) => {
    if (liveBySerial.has(serial)) return;
    const hlink = storeHlinks[serial] || {};
    const sensorMap = hlink.sensors || {};
    const sensors = Object.keys(sensorMap)
      .map((id) => Number(id))
      .filter((id) => isMapEligibleSensor(id, sensorMap[String(id)]?.sensor_type))
      .sort((a, b) => a - b)
      .map((sensorId) => {
        const install = sensorMap[String(sensorId)] || {};
        return {
          sensor_id: sensorId,
          com_state: 0,
          arc_state: false,
          arc_level: 0,
          ir_temperature: null,
          threshold_value: 0,
          install,
          position: resolveMarkerPosition(install, sensorId),
          label: `ID ${sensorId}`,
          locationLabel:
            install.installation_location ||
            install.installation_info ||
            "",
        };
      });

    // Show entry if it has a map image or any sensor install data
    if (!hlink.map_image && sensors.length === 0) return;

    liveBySerial.set(serial, {
      index: liveBySerial.size + 1,
      serial,
      hlink_ip: hlink.hlink_ip || "",
      install: hlink,
      map_image: hlink.map_image || null,
      sensors,
      live: false,
    });
  });

  return Array.from(liveBySerial.values()).map((row, i) => ({
    ...row,
    index: i + 1,
  }));
};
